import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaShieldAlt, FaPlus, FaPen, FaArchive, FaKey, FaPaperclip, FaLock, FaExternalLinkAlt, FaStar, FaRegStar } from "react-icons/fa";
import { VAULT_CONSTANTS } from "../_MODULES/_API";
import { VaultGroupFormModal } from "./VaultGroupFormModal";
import { OPTIONS_VAULT_KIND, onFindOptionLabel } from "../../../_Catalogs.Views";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { Card, EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { useDateRange, onMatchesRange } from "../../../../_UI/useDateRange";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";
import "./_Styles/Vault.css";

export const PanelVault = ({ companyId }) => {

    const [useSearch, setSearch] = useState("");

    const DATE_RANGE = useDateRange();
    const [useGroupEdit, setGroupEdit] = useState(null);
    const [useIsModalOpen, setIsModalOpen] = useState(false);

    const { apiGet: onGetGroups, useApiData: GROUPS, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 250, enablePagination: false, startLoading: true,
    });

    const { actionMutate } = useApiActionMutation({ isToastError: true, isToastSuccess: true, minTimeMutate: 300 });

    const onLoadGroups = () => onGetGroups({
        endpoint: VAULT_CONSTANTS.GROUP_BY_COMPANY, isDetail: true, config: { pathParams: { companyId } },
    });

    useEffect(() => { onLoadGroups(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

    const onOpenCreate = () => {
        setGroupEdit(null);
        setIsModalOpen(true);
    };

    const onOpenEdit = (group) => {
        setGroupEdit(group);
        setIsModalOpen(true);
    };

    const onArchive = async (group) => {

        const IS_CONFIRMED = await confirm.danger(
            "Archivar grupo",
            `Se archiva "${group.name}" con sus accesos y archivos. Podés restaurarlo desde Archivados.`,
        );

        if (!IS_CONFIRMED) return;

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: VAULT_CONSTANTS.GROUP_ARCHIVE,
            pathParams: { id: group.id }, successMessage: "Grupo archivado",
        });

        if (RESPONSE.success) mutateLocal({ id: group.id, remove: true });
    };

    const onToggleFavorite = async (group) => {
        const previous = !!group.isFavorite;
        const next = !previous;
        mutateLocal({ id: group.id, patch: { isFavorite: next } });

        const RESPONSE = await onSilentPatch({
            endpoint: VAULT_CONSTANTS.GROUP_UPDATE,
            id: group.id,
            data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado del grupo.",
        });

        if (!RESPONSE.success) mutateLocal({ id: group.id, patch: { isFavorite: previous } });
    };

    const ITEMS = GROUPS ?? [];

    const VISIBLE = [...ITEMS].filter((group) => onMatchesRange(group, DATE_RANGE.useRange, "createdAt"))
        .filter((group) => onMatchesSearch(useSearch, [
        group.name, group.description, group.url, group.kind,
        ...(group.entries ?? []).flatMap((entry) => [entry.title, entry.email, entry.username, entry.host]),
    ]))
        .sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || String(a.name ?? "").localeCompare(String(b.name ?? "")));


    const EXPORT_COLUMNS = [
        { key: "name", label: "Grupo" },
        { key: "kind", label: "Tipo" },
        { key: "url", label: "Panel" },
        { key: "description", label: "Descripción" },
        { key: "entries", label: "Accesos" },
        { key: "files", label: "Archivos" },
    ];

    const EXPORT_ROWS = VISIBLE.map((group) => ({
        name: group.name,
        kind: onFindOptionLabel(OPTIONS_VAULT_KIND, group.kind),
        url: group.url ?? "",
        description: group.description ?? "",
        entries: group.entries?.length ?? 0,
        files: group.files?.length ?? 0,
    }));

    const PAGER = usePagedList(VISIBLE, 6, "groupPage");

    return (
        <>
            <div className="LStudio__app_panel_bar">

                <ViewToolbar
                    filters={
                        <SearchFilter value={useSearch} onChange={setSearch} total={ITEMS.length} matches={VISIBLE.length}
                            placeholder="Ej.: Repositorio GitLab o servidor de producción..." />
                    }
                    dateRange={DATE_RANGE}
                    total={PAGER.total}
                    perPage={PAGER.usePerPage}
                    onChangePerPage={PAGER.onChangePerPage}
                    exportColumns={EXPORT_COLUMNS}
                    exportRows={EXPORT_ROWS}
                    exportName="boveda"
                    actions={<AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo grupo</AppButton>} />

            </div>

            <p className="LStudio__app_todos_hint">
                Acá van las contraseñas que no dependen de ningún proyecto: hosting, paneles, consolas, servidores.
                Agrupá por servicio y dentro cargá cada acceso con su explicación y sus archivos.
            </p>

            {IS_LOADING && <Skeleton variant="box" height={160} count={2} />}

            {
                !IS_LOADING && ITEMS.length > 0 && VISIBLE.length === 0 && (
                    <EmptyState icon={<FaShieldAlt />} title="Sin coincidencias"
                        description={`Ningún grupo ni acceso coincide con "${useSearch}".`} />
                )
            }

            {
                !IS_LOADING && ITEMS.length === 0 && (
                    <EmptyState icon={<FaShieldAlt />} title="La bóveda está vacía"
                        description="Creá un grupo por servicio, tipo Hostinger o Proxmox, y cargá dentro sus accesos."
                        action={<AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo grupo</AppButton>} />
                )
            }

            {
                !IS_LOADING && VISIBLE.length > 0 && (
                    <div className="LStudio__app_vault_grid">
                        {
                            PAGER.paged.map((group) => (
                                <Card key={group.id} className="LStudio__app_vault_card">

                                    <div className="LStudio__app_vault_card_head">

                                        <span className="LStudio__app_vault_card_dot"
                                            style={{ background: group.color || "var(--LS__app_priv_accent, #e83a52)" }} />

                                        <Link to={`/companies/${companyId}/vault/${group.id}`}
                                            className="LStudio__app_vault_card_name">
                                            {group.name}
                                            {group.isFavorite && <FaStar className="LStudio__app_record_star" title="Destacado" />}
                                        </Link>

                                        <Badge tone="neutral" size="sm">
                                            {onFindOptionLabel(OPTIONS_VAULT_KIND, group.kind)}
                                        </Badge>

                                        {group.hasAccessPin && <FaLock className="LStudio__app_vault_card_lock" title="Protegido con pin" />}

                                        <CardActionMenu title="Acciones del grupo" items={[
                                            { key: "favorite", icon: group.isFavorite ? <FaStar /> : <FaRegStar />,
                                                label: group.isFavorite ? "Quitar de destacados" : "Destacar", onClick: () => onToggleFavorite(group) },
                                            { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => onOpenEdit(group) },
                                            { divider: true },
                                            { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchive(group) },
                                        ]} />

                                    </div>

                                    {group.description && <p className="LStudio__app_vault_card_desc">{group.description}</p>}

                                    {
                                        group.url && (
                                            <a href={group.url} target="_blank" rel="noreferrer"
                                                className="LStudio__app_vault_card_url">
                                                {group.url.replace(/^https?:\/\//, "")} <FaExternalLinkAlt />
                                            </a>
                                        )
                                    }

                                    <div className="LStudio__app_vault_card_meta">

                                        <span><FaKey /> {group.entries?.length ?? 0} accesos</span>

                                        <span><FaPaperclip /> {group.files?.length ?? 0} archivos</span>

                                    </div>

                                    <div className="LStudio__app_vault_card_actions">
                                        <AppButton size="sm" variant="soft"
                                            to={`/companies/${companyId}/vault/${group.id}`}>Abrir</AppButton>
                                    </div>

                                </Card>
                            ))
                        }
                    </div>
                )
            }

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            <VaultGroupFormModal
                isOpen={useIsModalOpen}
                companyId={companyId}
                group={useGroupEdit}
                onClose={() => setIsModalOpen(false)}
                onSaved={(saved, isEdit) => mutateLocal(isEdit
                    ? { id: saved.id, patch: saved }
                    : { insert: saved })} />
        </>
    );
};
